import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { TrendingUp, Users, ChevronDown, X, Store, Star, ClipboardCheck, Search, Handshake } from "lucide-react";
import { usePreviewSafeData } from '../hooks/usePreviewSafeData';
import CategoryHero from "./CategoryHero";
import NewsTicker from './NewsTicker';
// CategoryNav removed

const MODELS_BG =
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780931862393-l37b6x.png";

const AUDIENCE_LOCATIONS = ["DFW", "USA", "Global"] as const;

const MODEL_TYPES = ["Beginner", "Professional", "Kids", "Plus Size"] as const;

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  city: "",
  instagramUrl: "",
  youtubeUrl: "",
  tiktokUrl: "",
  facebookUrl: "",
  height: "",
  weight: "",
  modelType: "",
  languagesSpoken: "",
  audienceLocation: "",
  barterCollaboration: "",
  paidCollaboration: "",
  participationFee: "",
  collaborationFeeRange: "",
  consentAgreed: false,
};

export default function Models() {
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ ...initialForm });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { insert, loading } = usePreviewSafeData("model_applications");

  // ---- Hire Models form ----
  const [hireFormOpen, setHireFormOpen] = useState(false);
  const [hireForm, setHireForm] = useState({
    businessName: "",
    category: "",
    budget: "",
    numModels: "",
    eventDate: "",
    specialRequirements: "",
  });
  const [hireErrors, setHireErrors] = useState<Record<string, string>>({});
  const [hireSubmitted, setHireSubmitted] = useState(false);
  const [hireError, setHireError] = useState<string | null>(null);
  const { insert: insertHire, loading: hireLoading } = usePreviewSafeData("model_hire_requests");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.fullName.trim()) errs.fullName = "Full Name is required";
    if (!form.email.trim()) errs.email = "Email Address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Enter a valid email";
    if (!form.phone.trim()) errs.phone = "Phone Number is required";
    if (!form.city.trim()) errs.city = "City is required";
    if (!form.consentAgreed) errs.consentAgreed = "You must agree to be contacted";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      await insert({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        instagramUrl: form.instagramUrl.trim(),
        youtubeUrl: form.youtubeUrl.trim(),
        tiktokUrl: form.tiktokUrl.trim(),
        facebookUrl: form.facebookUrl.trim(),
        height: form.height.trim(),
        weight: form.weight.trim(),
        modelType: form.modelType,
        languagesSpoken: form.languagesSpoken.trim(),
        audienceLocation: form.audienceLocation,
        barterCollaboration: form.barterCollaboration,
        paidCollaboration: form.paidCollaboration,
        participationFee: form.participationFee,
        collaborationFeeRange: form.collaborationFeeRange.trim(),
        consentAgreed: form.consentAgreed,
        submittedAt: new Date().toISOString(),
      });
      setSubmitted(true);
      setForm({ ...initialForm });
      setErrors({});
    } catch (err) {
      setSubmitError(err instanceof Error && err.message ? "Your application could not be submitted. Please try again. (" + err.message + ")" : "Your application could not be submitted. Please try again.");
      console.error("Submission failed:", err);
    }
  };

  // ---- Hire form handlers ----
  const handleHireChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setHireForm((prev) => ({ ...prev, [name]: value }));
    if (hireErrors[name]) {
      setHireErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateHire = () => {
    const errs: Record<string, string> = {};
    if (!hireForm.businessName.trim()) errs.businessName = "Business Name is required";
    if (!hireForm.category.trim()) errs.category = "Category is required";
    if (!hireForm.budget.trim()) errs.budget = "Budget is required";
    if (!hireForm.numModels.trim()) errs.numModels = "Number of Models is required";
    return errs;
  };

  const handleHireSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateHire();
    if (Object.keys(errs).length) {
      setHireErrors(errs);
      return;
    }
    try {
      await insertHire({
        businessName: hireForm.businessName.trim(),
        category: hireForm.category.trim(),
        budget: hireForm.budget.trim(),
        numModels: hireForm.numModels.trim(),
        eventDate: hireForm.eventDate,
        specialRequirements: hireForm.specialRequirements.trim(),
        submittedAt: new Date().toISOString(),
      });
      setHireSubmitted(true);
      setHireForm({
        businessName: "",
        category: "",
        budget: "",
        numModels: "",
        eventDate: "",
        specialRequirements: "",
      });
      setHireErrors({});
    } catch (err) {
      setHireError(err instanceof Error && err.message ? "Your request could not be submitted. Please try again. (" + err.message + ")" : "Your request could not be submitted. Please try again.");
      console.error("Hire request submission failed:", err);
    }
  };

  const fieldClass =
    "w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-[#1f2937] bg-white focus:ring-2 focus:ring-[#51ba4f] focus:border-transparent outline-none transition-shadow";
  const labelClass = "block text-sm font-semibold text-[#1f2937] mb-1";
  const sectionClass = "border-b border-gray-200 pb-5 mb-5 last:border-0 last:pb-0 last:mb-0";

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Models"
        placeholder="Search Models…"
        backgroundImage={MODELS_BG}
      />
      <NewsTicker />
      {/* CategoryNav removed */}

      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-left"
        >
          <h2
            className="text-lg md:text-xl font-bold text-[#1f2937]"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Dallas Models & Businesses — Connect, Collaborate, Grow
          </h2>

          {/* CTA buttons removed — use the Split Feature Cards below to open forms */}

          {/* Subtitle */}
          <p className="text-sm md:text-base text-gray-600 mt-4 max-w-2xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Dallas Scoop connects DFW businesses with local models for food, lifestyle, fashion, and event collaborations.
          </p>
          <p className="text-xs text-gray-400 mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Real talent. Real businesses. Real local impact.
          </p>
        </motion.div>

        {/* Toggle form */}
        <AnimatePresence>
          {formOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="overflow-hidden mt-6"
            >
              <div className="max-w-2xl bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm">
                {submitted ? (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center py-8"
                  >
                    <div className="text-4xl mb-3">🎉</div>
                    <h3 className="text-xl font-bold text-[#1f2937] mb-2">
                      Application Submitted!
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Thanks for applying! We'll review your profile and reach
                      out if there's a fit.
                    </p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormOpen(false);
                      }}
                      className="text-sm font-semibold text-[#51ba4f] hover:underline"
                    >
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-0">
                    {/* Personal Information */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="fullName"
                            value={form.fullName}
                            onChange={handleChange}
                            placeholder="Your full name"
                            className={fieldClass}
                          />
                          {errors.fullName && (
                            <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="you@example.com"
                            className={fieldClass}
                          />
                          {errors.email && (
                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="phone"
                            type="tel"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="(555) 555-5555"
                            className={fieldClass}
                          />
                          {errors.phone && (
                            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="city"
                            value={form.city}
                            onChange={handleChange}
                            placeholder="Dallas, Frisco, Plano..."
                            className={fieldClass}
                          />
                          {errors.city && (
                            <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Social Media */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Social Media
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>Instagram Profile URL</label>
                          <input
                            name="instagramUrl"
                            value={form.instagramUrl}
                            onChange={handleChange}
                            placeholder="https://instagram.com/..."
                            className={fieldClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>YouTube URL</label>
                          <input
                            name="youtubeUrl"
                            value={form.youtubeUrl}
                            onChange={handleChange}
                            placeholder="https://youtube.com/@..."
                            className={fieldClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>TikTok URL</label>
                          <input
                            name="tiktokUrl"
                            value={form.tiktokUrl}
                            onChange={handleChange}
                            placeholder="https://tiktok.com/@..."
                            className={fieldClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Facebook Page URL</label>
                          <input
                            name="facebookUrl"
                            value={form.facebookUrl}
                            onChange={handleChange}
                            placeholder="https://facebook.com/..."
                            className={fieldClass}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Model Information */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Model Information
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClass}>Height</label>
                            <input
                              name="height"
                              value={form.height}
                              onChange={handleChange}
                              placeholder={'e.g. 5\'8"'}
                              className={fieldClass}
                            />
                          </div>
                          <div>
                            <label className={labelClass}>Weight</label>
                            <input
                              name="weight"
                              value={form.weight}
                              onChange={handleChange}
                              placeholder="e.g. 130 lbs"
                              className={fieldClass}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={labelClass}>Model Type</label>
                          <div className="flex flex-wrap gap-3">
                            {MODEL_TYPES.map((type) => (
                              <label
                                key={type}
                                className={`px-4 py-2 rounded-full text-xs font-semibold border cursor-pointer transition-colors ${
                                  form.modelType === type
                                    ? "bg-[#51ba4f] text-white border-[#51ba4f]"
                                    : "bg-white text-[#1f2937] border-gray-300 hover:border-[#51ba4f]"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="modelType"
                                  value={type}
                                  checked={form.modelType === type}
                                  onChange={handleChange}
                                  className="sr-only"
                                />
                                {type}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className={labelClass}>Languages Spoken</label>
                          <input
                            name="languagesSpoken"
                            value={form.languagesSpoken}
                            onChange={handleChange}
                            placeholder="English, Hindi, Telugu..."
                            className={fieldClass}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Audience Location */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Audience Location
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        {AUDIENCE_LOCATIONS.map((loc) => (
                          <label
                            key={loc}
                            className={`px-4 py-2 rounded-full text-xs font-semibold border cursor-pointer transition-colors ${
                              form.audienceLocation === loc
                                ? "bg-[#51ba4f] text-white border-[#51ba4f]"
                                : "bg-white text-[#1f2937] border-gray-300 hover:border-[#51ba4f]"
                            }`}
                          >
                            <input
                              type="radio"
                              name="audienceLocation"
                              value={loc}
                              checked={form.audienceLocation === loc}
                              onChange={handleChange}
                              className="sr-only"
                            />
                            {loc}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Compensation Type */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Compensation Type
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className={labelClass}>
                            Paid — Model expects monetary compensation
                          </label>
                          <div className="flex gap-4">
                            {["Yes", "No"].map((opt) => (
                              <label
                                key={opt}
                                className={`px-4 py-2 rounded-full text-xs font-semibold border cursor-pointer transition-colors ${
                                  form.barterCollaboration === opt
                                    ? "bg-[#51ba4f] text-white border-[#51ba4f]"
                                    : "bg-white text-[#1f2937] border-gray-300 hover:border-[#51ba4f]"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="barterCollaboration"
                                  value={opt}
                                  checked={form.barterCollaboration === opt}
                                  onChange={handleChange}
                                  className="sr-only"
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className={labelClass}>
                            Barter - Model is open to non-monetary compensation
                            such as products, services, or exposure
                          </label>
                          <div className="flex gap-4">
                            {["Yes", "No"].map((opt) => (
                              <label
                                key={opt}
                                className={`px-4 py-2 rounded-full text-xs font-semibold border cursor-pointer transition-colors ${
                                  form.paidCollaboration === opt
                                    ? "bg-[#51ba4f] text-white border-[#51ba4f]"
                                    : "bg-white text-[#1f2937] border-gray-300 hover:border-[#51ba4f]"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="paidCollaboration"
                                  value={opt}
                                  checked={form.paidCollaboration === opt}
                                  onChange={handleChange}
                                  className="sr-only"
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className={labelClass}>
                            Participation Fee — Model is willing to pay a fee
                            to participate
                          </label>
                          <div className="flex gap-4">
                            {["Yes", "No"].map((opt) => (
                              <label
                                key={opt}
                                className={`px-4 py-2 rounded-full text-xs font-semibold border cursor-pointer transition-colors ${
                                  form.participationFee === opt
                                    ? "bg-[#51ba4f] text-white border-[#51ba4f]"
                                    : "bg-white text-[#1f2937] border-gray-300 hover:border-[#51ba4f]"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="participationFee"
                                  value={opt}
                                  checked={form.participationFee === opt}
                                  onChange={handleChange}
                                  className="sr-only"
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className={labelClass}>
                            Fee Range (For Paid and Participation Fee Type)
                          </label>
                          <input
                            name="collaborationFeeRange"
                            value={form.collaborationFeeRange}
                            onChange={handleChange}
                            placeholder="$100 - $500, $500 - $1000..."
                            className={fieldClass}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Consent */}
                    <div className={sectionClass}>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="consentAgreed"
                          checked={form.consentAgreed}
                          onChange={handleChange}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#51ba4f] focus:ring-[#51ba4f]"
                        />
                        <span className="text-sm text-[#1f2937]">
                          I agree to be contacted by Dallas Scoop regarding
                          collaboration opportunities.
                        </span>
                      </label>
                      {errors.consentAgreed && (
                        <p className="text-red-500 text-xs mt-1 ml-7">
                          {errors.consentAgreed}
                        </p>
                      )}
                    </div>

                    {submitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-[12.5px] rounded-xl px-4 py-3 mb-3">
                        {submitError}
                      </div>
                    )}
                    {/* Submit */}
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={loading}
                        className="bg-[#51ba4f] hover:bg-[#43a041] text-white text-sm font-bold px-6 py-2.5 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {loading ? "Submitting…" : "Join Us"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormOpen(false)}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-4 py-2.5 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hire Models form */}
        <AnimatePresence>
          {hireFormOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="overflow-hidden mt-6"
            >
              <div className="max-w-2xl bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm">
                {hireSubmitted ? (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center py-8"
                  >
                    <div className="text-4xl mb-3">🎉</div>
                    <h3 className="text-xl font-bold text-[#1f2937] mb-2">
                      Request Submitted!
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Thanks for your interest! We'll review your request and get
                      back to you soon.
                    </p>
                    <button
                      onClick={() => {
                        setHireSubmitted(false);
                        setHireFormOpen(false);
                      }}
                      className="text-sm font-semibold text-[#51ba4f] hover:underline"
                    >
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleHireSubmit} className="space-y-0">
                    {/* Business Information */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Business Information
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className={labelClass}>
                            Business Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="businessName"
                            value={hireForm.businessName}
                            onChange={handleHireChange}
                            placeholder="Search for your business..."
                            className={fieldClass}
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Not listed on Dallas Scoop yet?{" "}
                            <a href="/add-business" className="text-[#51ba4f] underline font-medium">
                              Please add your business first
                            </a>
                          </p>
                          {hireErrors.businessName && (
                            <p className="text-red-500 text-xs mt-1">{hireErrors.businessName}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Model Category: Runway/Fashion/Kids/Plus Size, etc. <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="category"
                            value={hireForm.category}
                            onChange={handleHireChange}
                            placeholder="e.g. Runway, Fashion, Kids, Plus Size..."
                            className={fieldClass}
                          />
                          {hireErrors.category && (
                            <p className="text-red-500 text-xs mt-1">{hireErrors.category}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Budget & Requirements */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Campaign Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            Budget <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="budget"
                            value={hireForm.budget}
                            onChange={handleHireChange}
                            placeholder="$500, $1000, $5000..."
                            className={fieldClass}
                          />
                          {hireErrors.budget && (
                            <p className="text-red-500 text-xs mt-1">{hireErrors.budget}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Number of Models Needed{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="numModels"
                            value={hireForm.numModels}
                            onChange={handleHireChange}
                            placeholder="1, 3, 5..."
                            className={fieldClass}
                          />
                          {hireErrors.numModels && (
                            <p className="text-red-500 text-xs mt-1">{hireErrors.numModels}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>Event Date</label>
                          <input
                            name="eventDate"
                            type="date"
                            value={hireForm.eventDate}
                            onChange={handleHireChange}
                            className={fieldClass}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Special Requirements */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Additional Info
                      </h3>
                      <div>
                        <label className={labelClass}>Special Requirements</label>
                        <textarea
                          name="specialRequirements"
                          value={hireForm.specialRequirements}
                          onChange={handleHireChange}
                          rows={3}
                          placeholder="Any specific requirements, preferred model types, deliverables..."
                          className={fieldClass}
                        />
                      </div>
                    </div>

                    {hireError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-[12.5px] rounded-xl px-4 py-3 mb-3">
                        {hireError}
                      </div>
                    )}
                    {/* Submit */}
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={hireLoading}
                        className="bg-[#51ba4f] hover:bg-[#43a041] text-white text-sm font-bold px-6 py-2.5 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {hireLoading ? "Submitting…" : "Submit Request"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setHireFormOpen(false)}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-4 py-2.5 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Split Feature Cards ── */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: For Businesses */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="w-11 h-11 rounded-lg bg-[#082f6d]/10 flex items-center justify-center mb-4">
              <Store className="w-5 h-5 text-[#082f6d]" />
            </div>
            <h3
              className="text-lg font-bold text-[#1f2937] mb-2"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Hire Models
            </h3>
            <p className="text-sm text-gray-600 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Promote your restaurant, event, or brand through local Dallas models who actually reach your audience.
            </p>
            <ul className="space-y-2 mb-5">
              {["Find relevant DFW models", "Run food, lifestyle & event campaigns", "Managed collaboration support through Dallas Scoop"].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#1f2937]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#51ba4f] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={() => { setHireFormOpen(true); setHireSubmitted(false); }}
              className="inline-flex items-center gap-1.5 bg-[#51ba4f] hover:bg-[#43a041] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <Users className="w-3.5 h-3.5" />
              Hire Models
            </button>
            <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Business must be listed on Dallas Scoop before requesting collaborations.
            </p>
          </motion.div>

          {/* Card 2: For Models */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="w-11 h-11 rounded-lg bg-[#51ba4f]/10 flex items-center justify-center mb-4">
              <Star className="w-5 h-5 text-[#51ba4f]" />
            </div>
            <h3
              className="text-lg font-bold text-[#1f2937] mb-2"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Join as Model
            </h3>
            <p className="text-sm text-gray-600 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Get discovered by local businesses across Dallas–Fort Worth looking for talent in food, lifestyle, fashion, and events.
            </p>
            <ul className="space-y-2 mb-5">
              {["Receive collaboration opportunities", "Work with restaurants, brands & events", "Grow your visibility in DFW talent network"].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#1f2937]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#51ba4f] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={() => { setFormOpen(true); setSubmitted(false); }}
              className="inline-flex items-center gap-1.5 bg-[#082f6d] hover:bg-[#062455] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <Star className="w-3.5 h-3.5" />
              Join Model Network
            </button>
          </motion.div>
        </div>

        {/* ── How It Works ── */}
        <div className="mt-20">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-xl md:text-2xl font-bold text-[#1f2937] text-center mb-10"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            How It Works
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ClipboardCheck, color: "#082f6d", step: "1", title: "Businesses Post Requests", desc: "List business or submit model campaign" },
              { icon: Search, color: "#51ba4f", step: "2", title: "Models Join Network", desc: "Talent registers with niche and audience info" },
              { icon: Handshake, color: "#f59e0b", step: "3", title: "Dallas Scoop Connects", desc: "We match the right talent with the right brands" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <div className="text-xs font-bold text-gray-400 mb-1">STEP {item.step}</div>
                <h3 className="text-base font-bold text-[#1f2937] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>{item.title}</h3>
                <p className="text-sm text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Trust / Positioning ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 text-center max-w-xl mx-auto"
        >
          <div className="w-12 h-0.5 bg-[#f59e0b] mx-auto mb-4 rounded-full" />
          <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Dallas Scoop is building a curated talent network across Dallas–Fort Worth, helping local businesses and models collaborate more efficiently.
          </p>
        </motion.div>

      </div>
    </div>
  );
}
